import {
  asLocale,
  DEFAULT_LOCALE,
  INTL_LOCALE,
  type Locale,
} from "@elyxeer/ui";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getItem, setItem, StorageKey } from "~/lib/storage";

/**
 * Locales a member can actually end up in. English is translated but held back until
 * release, so a stored "en" or an English device still lands in Czech. The admin panel
 * is unaffected. Re-enable by adding "en" back (and the settings row and route).
 */
export const MEMBER_LOCALES: readonly Locale[] = ["cs" /* , "en" */];

function asMemberLocale(value: string | null | undefined): Locale {
  const locale = asLocale(value);
  return MEMBER_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
}

/** Read the stored preference, falling back to the device language. */
export async function detectLocale(): Promise<Locale> {
  const stored = await getItem(StorageKey.Locale);
  if (stored) return asMemberLocale(stored);
  return asMemberLocale(
    typeof navigator !== "undefined" ? navigator.language : null,
  );
}

export interface LocaleApi {
  locale: Locale;
  setLocale: (next: Locale) => Promise<void>;
  /** Locale-aware date formatting, so catalogues never carry a date format. */
  formatDate: (
    value: string | Date,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
  /** Localized month and year, with the month inflected for a date phrase. */
  formatMonthYear: (value: string | Date) => string;
  /**
   * Locale-aware money formatting for Shopify's decimal-string amounts
   * (cs → "2 916 Kč", en → "CZK 2,916"). Whole amounts drop the decimals.
   */
  formatCurrency: (amount: string, currencyCode: string) => string;
}

export function useLocale(): LocaleApi {
  const { i18n } = useTranslation();
  const locale = asLocale(i18n.language);

  const setLocale = useCallback(
    async (next: Locale) => {
      await i18n.changeLanguage(next);
      document.documentElement.lang = next;
      await setItem(StorageKey.Locale, next);
    },
    [i18n],
  );

  const formatDate = useMemo(() => {
    const defaultFormat: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return (value: string | Date, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(
        INTL_LOCALE[locale],
        options ?? defaultFormat,
      ).format(new Date(value));
  }, [locale]);

  const formatCurrency = useMemo(() => {
    return (amount: string, currencyCode: string) => {
      const value = Number(amount);
      return new Intl.NumberFormat(INTL_LOCALE[locale], {
        style: "currency",
        currency: currencyCode,
        // Shopify sends "2916.0"; showing ",00" on every price is noise, but a
        // genuinely fractional amount must keep its decimals.
        minimumFractionDigits: 0,
        maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
      }).format(value);
    };
  }, [locale]);

  const formatMonthYear = useMemo(() => {
    // Czech uses nominative for a standalone month ("srpen 2026") but genitive in
    // a date phrase ("1. srpna 2026"). Supplying the day gives Intl the grammatical
    // context; formatToParts then lets the UI omit that day without losing inflection.
    const formatter = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return (value: string | Date) =>
      formatter
        .formatToParts(new Date(value))
        .filter((part) => part.type === "month" || part.type === "year")
        .map((part) => part.value)
        .join(" ");
  }, [locale]);

  return { locale, setLocale, formatDate, formatMonthYear, formatCurrency };
}
